import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/nova/components';
import { useDeviceAnchor, usePreviewStage } from '../../../../components/preview-stage';
import { useConfigurator } from '../../../../store/configurator';
import { findThemeScreenshot } from '../../../../data/preview';
import { usePreviewSlot } from '../../../../lib/staticPreview';
import { useIsTablet } from '../../../../lib/deviceType';
import { LAPTOP_FRAME_DEFAULT } from './frame-art';
import { StaticMacDevice, StaticPhoneDevice } from './StaticDevices';
import {
  STAGE_W,
  STAGE_H,
  Root,
  Header,
  Heading,
  Title,
  TitleAccent,
  Subtitle,
  PreviewButton,
  PreviewButtonLabel,
  Stage,
  StageGroup,
  MacSlot,
  PhoneSlot,
  MacAnchor,
  PhoneAnchor,
  Loading,
  LoadingText,
} from './DevicePreview.styles';

function GlobeIcon(): React.ReactElement {
  // Uses currentColor (inherited white) — no colour literal needed.
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

/**
 * Finish-step main viewport: a headline + "Live Preview" pill, and a laptop+phone
 * device group showing the configured site. (The fixed Whitelabels.com lockup is
 * owned by WizardLayout, top-left, so it stays put across step changes.)
 *
 * Each device screen is a STATIC screenshot by default (no live iframe): the backend
 * pre-renders one per (theme, colour, art-style, locale) and the laptop/phone show its
 * `desktop`/`iphone` capture respectively. If the backend hasn't generated one for the
 * current selection (or the image itself fails to load) that device falls back to the
 * SAME live, warm iframe the build steps use (via the persistent {@link PreviewStage}
 * host) so it is never left blank. "Live Preview" (fullscreen) always opens the real
 * site regardless of whether the inline screens are static or fell back to live.
 */
export const DevicePreview = (): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const { previewUrl, selectedTheme, selectedColor, selectedStyle } = useConfigurator();
  const { openFullscreen } = usePreviewStage();
  const [scale, setScale] = React.useState(1);

  const lang = (i18n.language || 'en').split('-')[0];
  const screenshot = findThemeScreenshot(selectedTheme, selectedColor, selectedStyle, lang);
  const mac = usePreviewSlot(screenshot?.desktop ?? '');
  const phone = usePreviewSlot(screenshot?.iphone ?? '');

  // On tablets the persistent iframe host is suppressed (it crashes iPad Safari), so a
  // device with no static screenshot must NOT fall back to the live iframe; it stays a
  // static device (flat brand fill, no spinner) instead of an anchor nothing floats over.
  const isTablet = useIsTablet();

  // Always registered (hook order must stay stable); only ATTACHED to an anchor
  // element below when that device's static screenshot is exhausted, so the host
  // only floats its live iframe over a device that actually needs the fallback.
  const macAnchorRef = useDeviceAnchor({ role: 'mac', mode: 'group', bezel: 'macDefault' });
  const phoneAnchorRef = useDeviceAnchor({ role: 'phone', mode: 'group', bezel: 'phone', phoneZoomWidth: 440 });

  // `<Stage>` doesn't exist yet while `previewUrl` is still resolving (the surface
  // renders `<Loading>` instead), so this MUST be a ref CALLBACK, not a plain ref read
  // in a mount-only effect: a `useRef` + `useEffect(..., [])` would run before Stage
  // ever mounts, find `stageRef.current` null, bail, and (with no deps to re-run on)
  // never measure again, leaving `scale` stuck at its `1` default forever regardless
  // of how large the viewport actually is. The callback instead fires exactly when
  // the node is attached (whenever that happens) and detached.
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const measure = React.useCallback((el: HTMLElement): void => {
    const { width, height } = el.getBoundingClientRect();
    // v2 parity: leave comfortable margins (fill factor) so the laptop doesn't
    // crowd the sidebar, and cap the size so the group stays mid-stage.
    setScale(Math.min((width * 0.92) / STAGE_W, (height * 0.92) / STAGE_H, 1.72));
  }, []);
  const stageRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      if (!el) return;
      measure(el);
      const ro = new ResizeObserver(() => measure(el));
      ro.observe(el);
      resizeObserverRef.current = ro;
    },
    [measure],
  );

  return (
    <Root>
      <Header>
        <Heading>
          <Title>
            <Text text="Meet your new " tag="span" />
            <TitleAccent text="Casino" tag="span" />
          </Title>
          <Subtitle text="Everything is ready for launch. Take a final look." tag="paragraph" />
        </Heading>
        {previewUrl ? (
          <PreviewButton type="button" data-ga-id="cfg-finish-preview-open" onClick={openFullscreen}>
            <PreviewButtonLabel text="Live Preview" tag="span" />
            <GlobeIcon />
          </PreviewButton>
        ) : null}
      </Header>

      {previewUrl ? (
        <Stage ref={stageRef}>
          <StageGroup $scale={scale}>
            <MacSlot>
              {mac.exhausted && !isTablet ? (
                <MacAnchor ref={macAnchorRef} />
              ) : (
                <StaticMacDevice
                  imageSrc={mac.src}
                  onImageError={mac.onError}
                  topColor={selectedColor?.topNav}
                  bgColor={selectedColor?.bg}
                  frame={LAPTOP_FRAME_DEFAULT}
                  spinner={!mac.exhausted}
                />
              )}
            </MacSlot>
            <PhoneSlot>
              {phone.exhausted && !isTablet ? (
                <PhoneAnchor ref={phoneAnchorRef} />
              ) : (
                <StaticPhoneDevice
                  imageSrc={phone.src}
                  onImageError={phone.onError}
                  topColor={selectedColor?.topNav}
                  bottomColor={selectedColor?.bottomNav}
                  bgColor={selectedColor?.bg}
                  spinner={!phone.exhausted}
                />
              )}
            </PhoneSlot>
          </StageGroup>
        </Stage>
      ) : (
        <Loading>
          <LoadingText text="Loading preview…" />
        </Loading>
      )}
    </Root>
  );
};

export default DevicePreview;
